<script lang="ts" setup>
import { ref, computed } from "vue";
import { useField } from "vee-validate";
import { ExclamationCircleIcon } from "@heroicons/vue/solid";
import BaseInput from "./_base.vue";

interface Props {
    name: string;
    locked?: boolean;
    multiple?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    name: undefined,
    locked: false,
});

const { value, errorMessage, meta, handleBlur, handleChange, validate } = useField<FileList>(props.name);

const hovering = ref(false);
const input = ref<HTMLInputElement>();

function dragover(event: Event) {
    event.preventDefault();
    hovering.value = true;
}

function dragleave(event: Event) {
    event.preventDefault();
    if (hovering.value) {
        hovering.value = false;
    }
}

function drop(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const dragEvent = event as DragEvent;
    const files = dragEvent.dataTransfer?.files;
    if (!files || !files.length || !input.value) return;
    input.value.files = files;
    input.value.dispatchEvent(new Event("input"));
    hovering.value = false;
}

function removeFile(index: number) {
    if (input.value && input.value.files && input.value.files.length) {
        const dt = new DataTransfer();
        let i = 0;

        for (let file of input.value.files) {
            if (i !== index) dt.items.add(file);
            i++;
        }

        input.value.files = dt.files;

        input.value.dispatchEvent(new Event("input"));
    }
}

const message = computed(() => {
    switch (document.documentElement.lang) {
        case "fr":
            return '<span class="text-primary group-hover:no-underline underline">Sélectionnez des fichiers</span> ou déposez-les ici.';
        
        case "en":
            return '<span class="text-primary group-hover:no-underline underline">Select</span> or drag files here.';

        default:
            return '<span class="text-primary group-hover:no-underline underline">Kies bestanden</span> of sleep ze hiernaartoe.';
    }
});
</script>

<template>
    <BaseInput :errorMessage="errorMessage" :meta="meta" :name="name">
        <div class="mt-2 relative z-[2]">
            <label
                :for="name"
                @dragover.prevent="dragover"
                @dragleave.prevent="dragleave"
                @drop.prevent="drop"
                class="transition-all min-h-[10.75rem] p-8 flex flex-col border border-gray-200 group items-center text-gray-500 space-y-1 justify-center"
                :class="[
                    hovering,
                    !value || value.length === 0 ? 'cursor-pointer' : 'pointer-events-none',
                    hovering ? 'bg-primary-300 border-primary !text-primary' : '',
                    locked ? 'opacity-50 bg-gray-100 pointer-events-none' : '',
                    errorMessage && meta.touched ? 'border-red-300 text-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500' : '',
                ]"
            >
                <ul class="flex flex-col items-center space-y-4" v-if="value && value.length">
                    <transition-group name="fade">
                        <li class="inline-flex items-center space-x-2" v-for="(file, index) in value" :key="`${file.name}_index`">
                            <svg v-if="file.type.indexOf('image') >= 0" class="w-auto h-8" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fill="currentColor" fill-rule="evenodd" d="M4 3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H4Zm12 12H4l4-8l3 6l2-4l3 6Z" clip-rule="evenodd" />
                            </svg>
                            <svg v-else class="w-auto h-8" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fill="currentColor" fill-rule="evenodd" d="M4 4a2 2 0 0 1 2-2h4.586A2 2 0 0 1 12 2.586L15.414 6A2 2 0 0 1 16 7.414V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Z" clip-rule="evenodd" />
                            </svg>
                            <span class="text-sm" v-html="file.name"></span>
                            <svg @click.prevent="removeFile(index)" class="hover:text-red w-auto h-4 cursor-pointer pointer-events-auto select-none" title="Remove file" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    fill="currentColor"
                                    fill-rule="evenodd"
                                    d="M10 18a8 8 0 1 0 0-16a8 8 0 0 0 0 16ZM8.707 7.293a1 1 0 0 0-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 1 0 1.414 1.414L10 11.414l1.293 1.293a1 1 0 0 0 1.414-1.414L11.414 10l1.293-1.293a1 1 0 0 0-1.414-1.414L10 8.586L8.707 7.293Z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </li>
                    </transition-group>
                </ul>
                <template v-else>
                    <p class="text-sm" v-html="message"></p>
                </template>
            </label>
            <input ref="input" :multiple="multiple" :name="name" :id="name" type="file" @input="handleChange" @change="handleChange" @blur="handleBlur" />
            <div v-if="errorMessage && meta.touched" class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ExclamationCircleIcon class="w-5 h-5 text-red-500" aria-hidden="true" />
            </div>
        </div>
    </BaseInput>
</template>

<style scoped lang="scss">
input {
    display: none;
}

.hovering {
    * {
        @apply pointer-events-none;
    }
}
</style>
